/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-use-before-define */
import MainLayout from "../../../layouts/MainLayout";
import Form from "../../../components/forms/Form";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useForm from "../../../hooks/useForm";
import { attachContractFields } from "../../../constants/forms/orderServiceFields";
import useBaseService from "../../../hooks/services/useBaseService";
import useNotification from "../../../hooks/useNotification";
import {
  setDefaultFieldValues,
  normalizeFormErrors,
  getMissingRequiredFields,
} from "../../../utils/objectUtils";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import { useQueryClient } from "@tanstack/react-query";
import { useSectionFields } from "../../../hooks/useSectionFields";
import useLoader from "../../../hooks/useLoader";
import { useOrgan } from "../../../hooks/useOrgan";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";
import { entities } from "../../../constants/entities";
import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";
import MovementEnum from "../../../enums/movement-enum";
import { Box, Typography } from "@mui/material";
import { maskCurrency } from "../../../utils/maskUtils";
import ItemListSection from "../../../components/forms/ItemListSection";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import useSelectOptions from "../../../hooks/useSelectOptions";

const AttachContractOrderServicePage = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { selectedOrgan } = useOrgan();

  const { showNotification } = useNotification();
  const {
    post: baseServicePost,
    get: baseServiceGet,
    getByColumn: baseServiceGetByColumn,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const {
    updateField: updateProps,
    removeFields,
    swapFieldOrder,
  } = useSectionFields();

  const { showLoader, hideLoader } = useLoader();

  const [attachContractFieldsSection, equipmentsFieldsSection] =
    attachContractFields;

  const { formData, handleChange, setFormData, resetForm } = useForm(
    setDefaultFieldValues([attachContractFieldsSection]),
    [attachContractFieldsSection],
  );

  const {
    formData: equipmentsFormData,
    handleChange: handleEquipmentChange,
    setFormData: setEquipmentsFormData,
  } = useForm(setDefaultFieldValues([equipmentsFieldsSection]), [
    equipmentsFieldsSection,
  ]);

  const [equipmentsFieldsState, setEquipmentsFieldsState] = useState(
    equipmentsFieldsSection.fields,
  );

  const { data: orderData } = useQueryWithLoader({
    queryKey: ["orderService", id],
    queryFn: async () => {
      const response = await baseServiceGetByColumn(
        entities.serviceOrders.getById(id),
      );

      return response.result;
    },
    select: (data) => ({
      order: {
        recipient_customer_id: {
          value: data.recipient_customer_id,
          label: data.recipient_customer?.name,
        },
        destination_order_id: {
          value: data.destination_order_id,
          label: data.destination_order?.name,
        },
        mode_transport_id: {
          value: data.mode_transport_id,
          label: data.mode_transport?.name,
        },
        movement_type_id: {
          value: data.movement_type_id,
          label: data.movement_type?.name,
        },
        deadline: data.deadline,
        details: data.details,
      },
    }),
  });

  const serviceOrderId = id;

  const { data: equipmentsData } = useQueryWithLoader({
    queryKey: ["contractOsItemsEquipments", serviceOrderId],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl({
        type: "equipment",
      });
      const params = {
        filters: filtersTransformed,
        sort: {
          column: "id",
          order: "asc",
        },
      };
      const response = await baseServiceGet(
        entities.serviceOrders.items(serviceOrderId),
        params,
      );
      return response.result.data;
    },
    enabled: !!serviceOrderId,
    refreshOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    select: (data) => ({
      equipments: data.map((item) => {
        return {
          id: item.id,
          department_id: {
            value: item.department_id,
            label: item.department?.name,
          },
          contract_item_id: item.contract_item_id && {
            value: item.contract_item_id,
            label: item.contract_item?.alias,
          },
          type_id: {
            value: item.type_id,
            label: item.type?.name,
          },
          address_id: {
            value: item.address_id,
            label: item.address?.formatted_address,
          },
          location_id: item.location_id && {
            value: item.location_id,
            label: item.location?.formatted_location,
          },
          details: item.details,
          quantity: item.quantity || 0,
          fulfilled_quantity: item.fulfilled_quantity || 0,
        };
      }),
    }),
  });

  useEffect(() => {
    if (orderData && equipmentsData) {
      setFormData((prevData) => ({
        ...prevData,
        order: {
          ...orderData.order,
        },
        equipments: {
          ...equipmentsData.equipments,
        },
      }));

      setEquipmentsFormData((prevData) => ({
        ...prevData,
        equipments: {
          ...equipmentsData.equipments,
        },
      }));

      setEquipments(equipmentsData.equipments);
    }
  }, [equipmentsData, orderData, setEquipmentsFormData, setFormData]);

  const recipientCustomerSelect = useRemoteSelectOptions({
    queryKey: ["customers"],
    baseServiceGet,
    columnDataBase: ["id", "alias"],
    extraFilters: {
      alias: { $nin: ["ALUCOM", "MOREIA", "IP", "AS SISTEMAS"] },
    },
    defaultSort: { column: "id", order: "desc" },
    entityPath: entities.customers.get,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
    initialId: formData.order.recipient_customer_id?.value,
    fieldId: "order.recipient_customer_id",
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
  });

  const contractSelect = useRemoteSelectOptions({
    queryKey: ["contracts"],
    baseServiceGet,
    columnDataBase: ["number", "name"],
    extraFilters: {
      organization_id: selectedOrgan?.id,
      $or: [
        { customer_id: { $eq: formData.order.recipient_customer_id?.value } },
        {
          "recipientCustomers.customer_id": {
            $in: [formData.order.recipient_customer_id?.value],
          },
        },
      ],
    },
    defaultSort: { column: "id", order: "desc" },
    entityPath: entities.contracts.get,
    fetcherById: async (id) => {
      const response = await baseServiceGetByColumn(
        entities.contracts.getById(id),
      );
      return response.result;
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.number} - ${item.name}`,
    }),
    fieldId: "order.contract_id",
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    immediateFetch: true,
    refetchOnWindowFocus: true,
  });

  const destinationsOrderSelect = useRemoteSelectOptions({
    queryKey: ["destinations_orders"],
    baseServiceGet,
    entityPath: entities.destinationOrders.get,
    enabled: !!recipientCustomerSelect.selectedValue?.value,
    extraFilters: {
      customer_id: { $eq: recipientCustomerSelect.selectedValue?.value },
    },
    defaultSort: { column: "created_at", order: "desc" },
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    fieldId: "order.destination_order_id",
    formData: formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const modeTransportSelect = useRemoteSelectOptions({
    queryKey: ["serviceOrderModeTransport"],
    baseServiceGet,
    entityPath: entities.serviceOrderModeTransports.get,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    fieldId: "order.mode_transport_id",
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const selects = useMemo(
    () => ({
      "order.recipient_customer_id": recipientCustomerSelect,
      "order.contract_id": contractSelect,
      "order.destination_order_id": destinationsOrderSelect,
      "order.mode_transport_id": modeTransportSelect,
    }),
    [
      contractSelect,
      modeTransportSelect,
      destinationsOrderSelect,
      recipientCustomerSelect,
    ],
  );

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects],
  );

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  const [equipments, setEquipments] = useState([]);
  const [lastSelectedAddress, setLastSelectedAddress] = useState(null);

  const [updatedLabelConst, setUpdatedLabelConst] = useState(null);

  const typeItemSelect = useSelectOptions({
    fieldId: "equipments.type_item_id",
    formData: equipmentsFormData,
    rawOptions: [
      { value: "item", label: "📦 Item Contrato" },
      { value: "extra", label: "🎁 Kit Equipamento" },
    ],
    isMulti: false,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
  });

  const contractItemSelect = useRemoteSelectOptions({
    queryKey: ["contractItems", contractSelect.selectedValue?.value],
    baseServiceGet,
    columnDataBase: "alias",
    entityPath: entities.contracts.items(contractSelect.selectedValue?.value),
    mapToOption: (item) => {
      const labelItem = updatedLabelConst?.find((p) => p.value === item.id);

      // Se não encontrou ou o restante é 0 ou menor, ignora
      if (!labelItem || labelItem.remainingQuantity <= 0) {
        return null; // <- Retorna null para excluir da lista
      }

      return {
        value: item.id,
        label: labelItem.label,
      };
    },
    fieldId: "equipments.contract_item_id",
    formData: equipmentsFormData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    enabled: !!contractSelect.selectedValue?.value,
    refetchOnWindowFocus: true,
    immediateFetch: true,
  });

  // Atualiza bloqueados dinamicamente
  useEffect(() => {
    if (!contractItemSelect.rawData) return;

    const usedIds = new Set(
      equipments.map((item) => item.contract_item_id?.value).filter(Boolean),
    );

    // Todos os IDs do rawData que NÃO estão no usedIds → esses serão bloqueados
    const newBlockedIds =
      typeItemSelect.selectedValue?.value === "extra"
        ? contractItemSelect.rawData
            .map((item) => item.id)
            .filter((id) => !usedIds.has(id)) // bloqueia o que não está no equipments
        : equipments
            .map((item) => item.contract_item_id?.value)
            .filter(Boolean);

    contractItemSelect.setBlockedIds(newBlockedIds);
  }, [typeItemSelect.selectedValue, contractItemSelect.rawData, equipments]);

  const departmentSelectEquipment = useRemoteSelectOptions({
    queryKey: ["serviceOrderEquipmentDepartament"],
    baseServiceGet,
    entityPath: entities.serviceOrderdepartments.get,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
    fieldId: "equipments.department_id",
    formData: equipmentsFormData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    immediateFetch: true,
  });

  const typesSelect = useRemoteSelectOptions({
    queryKey: ["equipmentTypes"],
    baseServiceGet,
    entityPath: entities.types.get,
    extraFilters: {
      "products.is_asset": { $eq: 1 },
    },
    fieldId: "equipments.type_id",
    isMulti: false,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
    formData: equipmentsFormData,
    setFormData,
    perPage: 15,
    immediateFetch: true,
    enablePagination: true,
  });

  const equipmentsSelects = useMemo(
    () => ({
      "equipments.department_id": departmentSelectEquipment,
      "equipments.contract_item_id": contractItemSelect,
      "equipments.type_item_id": typeItemSelect,
      "equipments.type_id": typesSelect,
    }),
    [
      departmentSelectEquipment,
      contractItemSelect,
      typeItemSelect,
      typesSelect,
    ],
  );

  const getOptionsEquipments = useCallback(
    (fieldId) => {
      const selectConfig = equipmentsSelects[fieldId];
      return selectConfig?.options || [];
    },
    [equipmentsSelects],
  );

  const getSelectedValueEquipments = useCallback(
    (fieldId) => {
      const selectConfig = equipmentsSelects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [equipmentsSelects],
  );

  const getSelectMetaEquipment = useCallback(
    (fieldId) => {
      const selectConfig = equipmentsSelects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [equipmentsSelects],
  );

  const updatedLabel = useMemo(() => {
    return contractItemSelect.rawData?.map((item) => {
      const isExtra = typeItemSelect.selectedValue?.value === "extra";

      return {
        label: isExtra
          ? `${item.alias || item.id} - ${item.description}`
          : `${item.alias || item.id} - ${item.description} (Total: ${item.quantity}, Restante: ${item.available_quantity})`,
        value: item.id,
        fullData: item,
        ...(isExtra ? {} : { remainingQuantity: item.available_quantity }),
      };
    });
  }, [contractItemSelect.rawData, typeItemSelect.selectedValue?.value]);

  useEffect(() => {
    setUpdatedLabelConst(updatedLabel);
  }, [updatedLabel, typeItemSelect.selectedValue?.value]);

  const handleEquipmentsChange = useCallback(
    (newEquipments) => {
      setEquipments(newEquipments);
      setFormData((prev) => ({ ...prev, equipments: newEquipments }));
    },
    [setFormData],
  );

  const renderFormBodyEquipment = useCallback(
    ({
      fields,
      formData,
      handleFieldChange,
      formErrors,
      getOptions,
      getSelectedValue,
      getSelectMeta,
    }) => {
      return (
        <SimpleBody
          fields={fields}
          formErrors={normalizeFormErrors(formErrors)}
          formData={formData}
          handleFieldChange={handleFieldChange}
          getOptions={getOptions}
          getSelectedValue={getSelectedValue}
          getSelectMeta={getSelectMeta}
        />
      );
    },
    [],
  );

  const getItemsDisplayValue = useCallback((item, fieldId) => {
    const fieldName = fieldId.split(".").pop();
    const value = item[fieldName];

    if (value && typeof value === "object" && "label" in value) {
      // se value.label tiver parenteses, remova tudo do paranteses em diante (para remover o (Total: 50, Restante: 50) dos itens do contrato)
      if (value.label.includes("(")) {
        return value.label.split("(")[0].trim();
      }
      return value.label;
    }

    if ((value && typeof value === "undefined") || !value) {
      return "";
    }

    return value;
  }, []);

  // Resetar o endereço persistido caso o usuário remova todos os equipamentos da lista
  useEffect(() => {
    if (equipments.length === 0) {
      setLastSelectedAddress(null);
    }
  }, [equipments]);

  const handleSaveEquipments = useCallback(
    (itemToEdit) => {
      const itemData = equipmentsFormData.equipments;
      const typeMovement = formData.order.movement_type_id?.value;

      const ignoreMap = {
        aluguel: ["equipments.equipment_id", "equipments.type_id"],
      };

      const missingFields = getMissingRequiredFields({
        itemData,
        fields: equipmentsFieldsSection.fields,
        prefix: "equipments",
        ignore: typeMovement === MovementEnum.ALUGUEL ? ignoreMap.aluguel : [],
      });

      if (missingFields.length > 0) {
        showNotification(
          "warning",
          "Preencha todos os campos obrigatórios para equipmento",
        );
        return false;
      }

      if (typeMovement === MovementEnum.ALUGUEL) {
        const contractItem = contractItemSelect.rawData?.find(
          (item) => item.id === itemData?.contract_item_id?.value,
        );

        if (contractItem) {
          const matchingItems = equipments.filter(
            (i) =>
              String(i.contract_item_id?.value) === String(contractItem.id) &&
              (!itemToEdit || i.id !== itemToEdit.id),
          );

          const totalUserQuantity = matchingItems.reduce(
            (acc, cur) => acc + Number(cur.quantity || 0),
            0,
          );

          const remainingQuantity =
            contractItem.quantity -
            (contractItem.quantity_awaiting_shipment +
              contractItem.quantity_on_the_way +
              contractItem.quantity_rented +
              contractItem.quantity_requested +
              totalUserQuantity);
          const newRequestedQuantity = itemData?.quantity || 0;

          if (newRequestedQuantity > remainingQuantity) {
            showNotification(
              "error",
              `Quantidade excedida. Disponível: ${remainingQuantity}`,
            );
            return;
          }
        }
      }

      // LOGICA DE PERSISTÊNCIA DO ENDEREÇO
      // Se o item tem um address_id selecionado, salvamos para o próximo
      if (itemData?.address_id) {
        setLastSelectedAddress(itemData.address_id);
      }

      const updatedEquipments = itemToEdit
        ? equipments.map((item) =>
            item.id === itemToEdit.id ? { ...item, ...itemData } : item,
          )
        : [...equipments, { id: uuidv4(), ...itemData }];

      setEquipments(updatedEquipments);
      handleEquipmentsChange(updatedEquipments);
      return true;
    },
    [
      equipmentsFormData.equipments,
      formData.order.movement_type_id?.value,
      equipmentsFieldsSection.fields,
      equipments,
      handleEquipmentsChange,
      showNotification,
      contractItemSelect.rawData,
    ],
  );

  const initializeEquipmentsForm = useCallback(
    (equipmentData) => {
      // Se não tiver dados de equipamento (novo item), aplica o defaultValue
      const dataToSet = equipmentData || {
        department_id: departmentSelectEquipment.selectedValue || {
          value: 2,
          label: "EXPEDIÇÃO",
        },
        // Injeta o último endereço selecionado se for um novo item
        address_id: lastSelectedAddress ? lastSelectedAddress : undefined,
      };

      setEquipmentsFormData({ equipments: dataToSet || {} });
    },
    [
      departmentSelectEquipment.selectedValue,
      setEquipmentsFormData,
      lastSelectedAddress,
    ],
  );

  useEffect(() => {
    const value = typeItemSelect.selectedValue?.value;

    if (!value) {
      setEquipmentsFieldsState(equipmentsFieldsSection.fields);
      return;
    }

    let updateEquipmentFields = [...equipmentsFieldsSection.fields];

    if (value === "item") {
      updateEquipmentFields = updateProps({
        arrayDependente: updateEquipmentFields,
        field_id: ["equipments.contract_item_id"],
        updates: {
          disabled: false,
          label: "Item Contrato",
        },
      });
    }

    if (value === "extra") {
      // updateEquipmentFields = swapFieldOrder({
      //   fields: updateEquipmentFields,
      //   field_id: ["equipments.contract_item_id", "equipments.type_id"],
      // });

      updateEquipmentFields = updateProps({
        arrayDependente: updateEquipmentFields,
        field_id: ["equipments.contract_item_id"],
        updates: {
          disabled: false,
          label: "Vinculado a",
        },
      });

      // * Precisamos mudar a option
    }

    setEquipmentsFieldsState(updateEquipmentFields);
  }, [
    typeItemSelect.selectedValue,
    swapFieldOrder,
    updateProps,
    equipmentsFieldsSection.fields,
  ]);

  async function handleSubmit() {
    console.log(formData);
    return;
  }

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Atrelar requisição ao contrato`}
        showBackButton={true}
        // backUrl={`/aluguel/contratos/${contractSelected?.id}/requisicoes/`}
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          textSubmit="Transformar"
          textLoadingSubmit="Transformando..."
          // handleBack={handleBack}
          // isLoading={createContractOsMutation.isPending}
        >
          {() => (
            <>
              <div key={attachContractFieldsSection.section}>
                <SectionHeader section={attachContractFieldsSection.section} />
                <SimpleBody
                  fields={attachContractFieldsSection.fields}
                  formErrors={normalizeFormErrors(formErrors)}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>
              <Box
                key={equipmentsFieldsSection.section}
                sx={{ mt: "1rem", display: "flex", flexDirection: "column" }}>
                {/* EQUIPAMENTOS */}
                <ItemListSection
                  section={equipmentsFieldsSection.section}
                  fields={equipmentsFieldsState}
                  initialData={equipments}
                  onItemsChange={handleEquipmentsChange}
                  renderFormBody={renderFormBodyEquipment}
                  getItemDisplayValue={getItemsDisplayValue}
                  itemFormData={equipmentsFormData}
                  onItemChange={handleEquipmentChange}
                  itemFormErrors={formErrors}
                  onFormSubmit={handleSaveEquipments}
                  initializeItemForm={initializeEquipmentsForm}
                  isDisabledButton={true}
                  getOptions={getOptionsEquipments}
                  getSelectedValue={getSelectedValueEquipments}
                  getSelectMeta={getSelectMetaEquipment}
                  // notShowHeader={["equipments.type_item_id"]}
                  hideColumnAciton={
                    contractSelect.selectedValue?.value ? false : true
                  }
                  disableActions={
                    contractSelect.selectedValue?.value
                      ? ["delete"]
                      : ["edit", "delete"]
                  }
                />
              </Box>
            </>
          )}
        </Form>
        {/* )} */}
      </div>
    </MainLayout>
  );
};

export default AttachContractOrderServicePage;
